import { useState } from 'react';

export interface PaginationState<TItem> {
  /** 1-based, and always a page that exists. */
  readonly page: number;
  readonly pageSize: number;
  /** The rows on the current page. */
  readonly items: readonly TItem[];
  readonly total: number;
  readonly setPage: (page: number) => void;
}

/**
 * A list paged in the browser: the whole array in, one page of it out.
 *
 * For the screens that already hold every row and filter them locally. A list
 * the API pages has its page in the request and belongs to `Pagination` alone;
 * slicing here what the server already sliced would page a page.
 *
 * Back to page 1 whenever the list changes, so that narrowing a filter never
 * leaves the reader on page 4 of a list that now has one. "Changes" means a
 * different set of rows, compared row by row, and not merely a different array:
 * a screen that filters during render hands over a new array every time it
 * renders, and comparing identities would send every press of Próxima straight
 * back to page 1. The comparison is a pass over a list the screen is already
 * passing over to filter it.
 *
 * The reset happens during render, by remembering the last list in state, so
 * there is never a frame showing the old page of the new list.
 */
export function usePagination<TItem>(
  rows: readonly TItem[],
  pageSize: number,
): PaginationState<TItem> {
  const [page, setPage] = useState(1);
  const [seen, setSeen] = useState(rows);

  let current = page;
  if (!sameRows(seen, rows)) {
    setSeen(rows);
    setPage(1);
    current = 1;
  }

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  current = Math.min(Math.max(1, current), pageCount);
  const start = (current - 1) * pageSize;

  return {
    page: current,
    pageSize,
    items: rows.slice(start, start + pageSize),
    total: rows.length,
    setPage,
  };
}

function sameRows<TItem>(left: readonly TItem[], right: readonly TItem[]): boolean {
  return (
    left === right || (left.length === right.length && left.every((row, i) => row === right[i]))
  );
}
